// Finance data hooks — polling hooks for /api/finance/* (FY26 levy position).
// All endpoints require board_member+ (gated in the worker). Default interval
// 120s — finance data updates on weekly cash-watch cadence, not real time.

import { usePolling } from "./useDashboard";
import { apiFetch } from "../lib/apiFetch";

export interface FinanceSummary {
  /** The (fund, FY) slice the worker resolved (0094 fund/fy data layer). */
  fiscal_year?: string;
  fund?: string;
  requested_fy?: string;
  /** True when the requested FY had no rows yet and an earlier FY is shown. */
  is_fallback?: boolean;
  as_of_date: string | null;
  funds_available: number;
  funds_available_note: string | null;
  open_pos: { count: number; encumbered: number; carryforward_count: number };
  /** Prior-fund carryover POs (own appropriation) — null when resolved fund is 2101. */
  carryover_pos?: { count: number; encumbered: number; fund: string } | null;
  expenditure: {
    budget: number;
    commitment: number;
    obligation: number;
    actual: number;
    consumed: number;
    funds_available: number;
  };
  revenue: { budget: number; actual: number };
}

export interface ByObjectRow {
  object_segment: string;
  object_desc: string;
  object_class: string;
  budget_amount: number;
  commitment_amount: number;
  obligation_amount: number;
  actual_amount: number;
  total_consumption: number;
  funds_available: number;
  [key: string]: unknown;
}

export interface OpenPoRow {
  po_number: string;
  remaining: number;
  carryforward: number;
  carryover: number;
  /** COA fund segment ('2119' operating / '2101' carryover in FY27). */
  fund?: string | null;
  fy_established: number | null;
  object_segment: string;
  object_desc: string;
  budget_date: string;
  supplier: string;
  status: string;
  funds_status: string;
  description: string;
  /** committee attribution from the register (may be null). */
  committee: string | null;
  [key: string]: unknown;
}

export interface ObjectWatchRow {
  object_segment: string;
  object_desc: string;
  budget: number;
  actual: number;
  open_encumbrance: number;
  open_pos: number;
  projected: number;
  pct_projected: number;
  flag: "over" | "tight" | "ok";
  [key: string]: unknown;
}

/** GET /api/finance/object-watch — budget vs actual vs open encumbrance by object. */
export function useObjectWatch(intervalMs = 120_000) {
  return usePolling<ListResponse<ObjectWatchRow>>(
    "/api/finance/object-watch",
    intervalMs,
  );
}

export interface VendorPoRow {
  po_number: string;
  supplier: string;
  total: number;
  description: string | null;
  status: string | null;
  funds_status: string | null;
  order_date: string | null;
  fiscal_year: string | null;
  committee: string | null;
  [key: string]: unknown;
}

/** Fetch the individual PO line items for one supplier (drill-down / modal). */
export async function fetchVendorPos(supplier: string): Promise<VendorPoRow[]> {
  const res = await apiFetch(
    `/api/finance/vendor-pos?supplier=${encodeURIComponent(supplier)}`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as ListResponse<VendorPoRow>;
  return json.data ?? [];
}

export interface TrendRow {
  as_of_date: string;
  funds_available: number;
  note: string | null;
  [key: string]: unknown;
}

export interface ListResponse<T> {
  data: T[];
  count: number;
}

/** GET /api/finance/summary — board headline (funds available, encumbrance, rollups). */
export function useFinanceSummary(intervalMs = 120_000) {
  return usePolling<FinanceSummary>("/api/finance/summary", intervalMs);
}

// ── Pipeline freshness (Mobius pull observability) ────────────────────────────
export type FreshnessStatus = "ok" | "stale" | "failed" | "unknown";
export interface SourceFreshness {
  source: "snapshot" | "bc_balance" | "open_po" | "full_xml";
  label: string;
  as_of: string | null;
  age_days: number | null;
  status: FreshnessStatus;
  origin: "pull_status" | "derived" | "none";
  row_count: number | null;
  note: string | null;
}
export interface FinanceFreshness {
  sources: SourceFreshness[];
  worst_age_days: number | null;
  any_stale: boolean;
  stale_threshold_days: number;
}
/** GET /api/finance/freshness — per-source "as of" + age for the staleness strip. */
export function useFinanceFreshness(intervalMs = 120_000) {
  return usePolling<FinanceFreshness>("/api/finance/freshness", intervalMs);
}

// ── Levy headline KPIs (fund 2101-65-6560 — PWC appropriation) ────────────────
export interface LevyKpis {
  /** The (fund, FY) slice the worker resolved (0094 fund/fy data layer). */
  fiscal_year?: string;
  fund?: string;
  requested_fy?: string;
  /** True when the requested FY had no rows yet and an earlier FY is shown. */
  is_fallback?: boolean;
  as_of_date: string | null;
  funds_available_note: string | null;
  budget_total: number;
  /** Portion of budget_total on the reserve class (58710 Reserves & Contingencies). */
  budget_reserve?: number;
  actual_total: number;
  encumbered_total: number;
  encumbered_count: number;
  /** Prior-fund carryover POs (own appropriation) — never in encumbered_total. */
  carryover_encumbered_total?: number;
  carryover_encumbered_count?: number;
  encumbered_remaining: number;
  remaining_balance: number;
  object_funds_available: number;
  remaining_balance_delta: number;
}
/** GET /api/finance/levy-kpis — top-row KPI totals for the Levy dashboard. */
export function useLevyKpis(intervalMs = 120_000) {
  return usePolling<LevyKpis>("/api/finance/levy-kpis", intervalMs);
}

// ── "Two ways to read funds available" reconciliation ─────────────────────────
// The captured authoritative pair (dash_finance_available): the all-object
// headline vs the County BCEA expenditure-only "Available Balance", plus the
// revenue-netting driver that explains the gap. Powers the always-visible
// explainer card on Overview + Levy and the Board Packet one-liner. Values are
// captured authoritative figures — NEVER reconstructed client-side.
export interface FinanceAvailable {
  as_of_date: string | null;
  /** FY the recon slice actually represents (resolver may fall back to a prior
   *  FY when the current FY has no recon rows yet — flagged via is_fallback). */
  fiscal_year?: string;
  is_fallback?: boolean;
  /** SINGLE SOURCE OF TRUTH headline — the nightly snapshot funds_available that
   *  ties to Oracle and is shown identically on every other surface. The
   *  reconciler leads with this so no two pages show a different "available". */
  snapshot_available: number | null;
  snapshot_as_of: string | null;
  /** snapshot_available − all_object_available; non-zero = the recon table is a
   *  pull behind the snapshot (expected freshness drift, disclosed in the UI). */
  tie_out_delta: number | null;
  /** Spending-side "budget remaining": Budget − Committed − Obligated − Actual,
   *  summed over expenditure objects (= what the by-object report sums to, the
   *  gross ~$158,751 figure). Overstates the controlling number. */
  expenditure_budget_remaining: number | null;
  /** Headline / Oracle dashverify — conservative, nets in uncollected revenue.
   *  This is the figure that actually gates spending. */
  all_object_available: number | null;
  /** County BCEA "Available Balance" — spending side only, revenue ignored. */
  expenditure_only_available: number | null;
  /** all_object_available − expenditure_only_available (the negative gap). */
  revenue_net: number | null;
  /** Object code of the single biggest gap driver (e.g. '41120'). */
  driver_object: string | null;
  driver_desc: string | null;
  driver_amount: number | null;
  note: string | null;
  updated_at: string | null;
}
/** GET /api/finance/available — the all-object vs BCEA funds-available pair. */
export function useFinanceAvailable(intervalMs = 120_000) {
  return usePolling<FinanceAvailable>("/api/finance/available", intervalMs);
}

// ── Non-profit (Truist + QuickBooks) operating-fund summary ───────────────────
export interface NonprofitKpis {
  cash_on_hand: number | null;
  income: number | null;
  expenses: number | null;
  net: number | null;
}
export interface NonprofitBankAccount {
  name: string;
  acct_num: string;
  balance: number;
  /** 1552 Payroll = board-designated set-aside, carved out of cash_available. */
  restricted: boolean;
  /** A parent account's direct-postings reconciling line (not a real account). */
  reconciling?: boolean;
}
export interface NonprofitSummary {
  connected: boolean;
  source: "quickbooks";
  bank: "truist";
  environment?: string;
  connected_by?: string | null;
  connect_url?: string;
  as_of: string | null;
  bank_accounts: NonprofitBankAccount[];
  cash_total: number | null;
  cash_restricted: number | null;
  cash_available: number | null;
  income_total: number | null;
  expense_total: number | null;
  net: number | null;
  ar_total: number | null;
  /** Back-compat KPI block (old shape); prefer the flattened fields above. */
  kpis: NonprofitKpis | null;
}
/** GET /api/finance/nonprofit/summary — QBO connection state + (when connected) cash/income/expense. */
export function useNonprofitSummary(intervalMs = 120_000) {
  return usePolling<NonprofitSummary>(
    "/api/finance/nonprofit/summary",
    intervalMs,
  );
}

export interface NonprofitIncomeRow {
  source: string;
  amount: number;
  /** PWC reimbursement passthrough — not the non-profit's own revenue. */
  is_pwc: boolean;
}
export interface NonprofitIncomeBySource {
  available: boolean;
  as_of: string | null;
  rows: NonprofitIncomeRow[];
  own_total: number;
  pwc_total: number;
  total: number;
}
/** GET /api/finance/nonprofit/income-by-source — current-period income grouped (own vs PWC). */
export function useNonprofitIncomeBySource(intervalMs = 120_000) {
  return usePolling<NonprofitIncomeBySource>(
    "/api/finance/nonprofit/income-by-source",
    intervalMs,
  );
}

export interface NonprofitExpenseRow {
  category: string;
  acct_num: string;
  amount: number;
}
export interface NonprofitExpensesByCategory {
  available: boolean;
  as_of: string | null;
  rows: NonprofitExpenseRow[];
  total: number;
}
/** GET /api/finance/nonprofit/expenses-by-category — current-period expense lines, sorted desc. */
export function useNonprofitExpensesByCategory(intervalMs = 120_000) {
  return usePolling<NonprofitExpensesByCategory>(
    "/api/finance/nonprofit/expenses-by-category",
    intervalMs,
  );
}

export interface NonprofitMonthPoint {
  month: string; // 'YYYY-MM-01'
  label: string; // 'Jul 2024'
  income: number;
  pwc_income: number;
  own_income: number;
  expense: number;
  net: number;
  [key: string]: unknown;
}
export interface NonprofitTimeseries {
  available: boolean;
  as_of: string | null;
  months: NonprofitMonthPoint[];
  reason?: string | null;
}
/** GET /api/finance/nonprofit/timeseries — monthly income/expense/net + own-vs-PWC split. */
export function useNonprofitTimeseries(intervalMs = 120_000) {
  return usePolling<NonprofitTimeseries>(
    "/api/finance/nonprofit/timeseries",
    intervalMs,
  );
}

/** GET /api/finance/by-object — object-level budgetary-control balances. */
export function useFinanceByObject(cls = "expenditure", intervalMs = 120_000) {
  return usePolling<ListResponse<ByObjectRow>>(
    `/api/finance/by-object?class=${encodeURIComponent(cls)}`,
    intervalMs,
  );
}

/** GET /api/finance/open-pos — open purchase orders (encumbrances). */
export function useFinanceOpenPos(intervalMs = 120_000) {
  return usePolling<ListResponse<OpenPoRow>>(
    "/api/finance/open-pos",
    intervalMs,
  );
}

/** GET /api/finance/trend — funds-available snapshots over time. */
export function useFinanceTrend(intervalMs = 120_000) {
  return usePolling<ListResponse<TrendRow>>("/api/finance/trend", intervalMs);
}

// ── Monthly FY levy position series (the single YTD Overview chart) ────────────
export interface FinanceMonthlyRow {
  period_label: string; // 'Jul-25' … 'Jun-26'
  period_date: string; // 'YYYY-MM-01'
  total_budget: number;
  expenditures_cum: number;
  /** Raw open-encumbrance balance — CAN be negative (budget_date bucketing). */
  obligations_balance: number;
  /** Display value for the chart's Encumbrances line: max(0, obligations_balance). */
  obligations_balance_floored: number;
  /** Spending-side budget remaining (expenditure-object basis) — NOT the headline. */
  remaining: number;
  expenditures_month: number;
  obligations_month: number;
  [key: string]: unknown;
}
export interface FinanceMonthlyResponse {
  data: FinanceMonthlyRow[];
  count: number;
  fiscal_year: string;
}
/** GET /api/finance/monthly — Budget/Actuals/Encumbrances/Remaining monthly series. */
export function useFinanceMonthly(intervalMs = 120_000) {
  return usePolling<FinanceMonthlyResponse>("/api/finance/monthly", intervalMs);
}

// ── Overview spend / encumbrance breakdown panels ─────────────────────────────
export interface SpentByObjectRow {
  object_segment: string;
  object_desc: string;
  spent: number;
  [key: string]: unknown;
}
/**
 * A committee row on /api/finance/overview-breakdown. The money field is
 * `committed`, NOT `spent`: it is SUM(dash_finance_po.total) — a gross PO-header
 * COMMITMENT — never validated actual expenditure, and never additive to
 * SpentByObjectRow.spent. The field was renamed server-side (2026-08); this type
 * kept declaring the legacy `spent` and only typechecked because of the index
 * signature below, which silently let `spent` read as a number that no longer
 * exists on the wire.
 */
export interface SpentByCommitteeRow {
  committee: string;
  committed: number;
  po_count: number;
  [key: string]: unknown;
}
export interface EncByVendorRow {
  supplier: string;
  encumbered: number;
  po_count: number;
  object_desc: string | null;
  is_capital: boolean;
  [key: string]: unknown;
}
export interface EncByObjectRow {
  object_desc: string;
  encumbered: number;
  po_count: number;
  is_capital: boolean;
  [key: string]: unknown;
}
export interface EncumbranceSplit {
  total: number;
  capital: number;
  capital_count: number;
  operating: number;
  operating_count: number;
}
export interface OverviewBreakdownResponse {
  fiscal_year: string;
  /** The FY the committee panel actually reflects — falls back to the latest FY
   *  with POs when the clock-FY has none yet (e.g. just after 7/1). */
  spent_by_committee_fy: string;
  spent_by_object: SpentByObjectRow[];
  spent_by_committee: SpentByCommitteeRow[];
  encumbered_by_vendor: EncByVendorRow[];
  encumbered_by_object: EncByObjectRow[];
  encumbrance_split: EncumbranceSplit;
}
/** GET /api/finance/overview-breakdown — spent + encumbered panels + capital split. */
export function useOverviewBreakdown(intervalMs = 120_000) {
  return usePolling<OverviewBreakdownResponse>(
    "/api/finance/overview-breakdown",
    intervalMs,
  );
}

// ── Purchase-Orders register + summary (the /finance/purchase-orders page) ────

export interface PoSummaryResponse {
  /** The REGISTER FY actually shown (may be a fallback when the requested FY has no register rows). */
  fiscal_year: string;
  /** The FY that was requested (clock default or explicit pick). */
  requested_fy?: string;
  /** True when `fiscal_year` is a fallback because `requested_fy` had no POs yet. */
  is_fallback?: boolean;
  // The (fund, FY) slices the levy-sourced KPI blocks actually read — served so
  // the UI can label the KPIs honestly even while the register FY lags (the
  // register feed refreshes on its own cadence and can trail the live KPIs).
  encumbrance_fy?: string;
  encumbrance_is_fallback?: boolean;
  operating_fy?: string;
  operating_fund?: string;
  operating_is_fallback?: boolean;
  kpis: {
    // Lead headline: OPERATING-FUND open encumbrance (2101 through FY26, 2119
    // from FY27), split current-FY vs carried-onto-the-operating-fund.
    open_encumbrance: number;
    open_po_count: number;
    current_fy_encumbrance: number;
    current_fy_count: number;
    carryover_encumbrance: number;
    carryover_count: number;
    // Prior-fund (2101) carryover POs riding their OWN swept appropriation —
    // never part of open_encumbrance. Zero when the operating fund is 2101.
    carryover_2101_encumbrance: number;
    carryover_2101_count: number;
    // Validated operating KPIs (operating levy fund expenditure+reserve · ties to Oracle).
    operating_budget: number;
    operating_spent: number;
    operating_pct: number;
    // De-emphasized: gross PO commitments (NOT spend; re-issue copies removed).
    gross_commitments: number;
    gross_po_count: number;
  };
  monthly_trend: {
    month: string;
    po_count: number;
    value: number;
    cumulative: number;
  }[];
  encumbrance_by_object: {
    object_desc: string;
    encumbered: number;
    po_count: number;
  }[];
  encumbrance_by_committee: {
    committee: string;
    encumbered: number;
    po_count: number;
  }[];
}

/** GET /api/finance/po-summary — current-FY KPIs + monthly trend + encumbrance split. */
export function usePoSummary(fy?: string, intervalMs = 120_000) {
  const qs = fy ? `?fy=${encodeURIComponent(fy)}` : "";
  return usePolling<PoSummaryResponse>(
    `/api/finance/po-summary${qs}`,
    intervalMs,
  );
}

export interface PoRegisterRow {
  po_number: string;
  supplier: string | null;
  description: string | null;
  committee: string | null;
  total: number;
  status: string | null;
  order_date: string | null;
  fiscal_year: string | null;
  remaining: number | null;
  object_desc: string;
  object_segment: string;
  /** Capital-outlay PO (apparatus/vehicle/equipment) — not operating spend. */
  is_capital: boolean;
  /** Participates in an Open+Closed duplicate/re-issue group. */
  is_reissue: boolean;
  [key: string]: unknown;
}
export interface PoRegisterResponse {
  data: PoRegisterRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  filters: {
    fy: string;
    status: string;
    committee: string | null;
    q: string | null;
  };
  /** The FY that was requested (clock default or explicit pick). */
  requested_fy?: string;
  /** True when the register fell back to a prior FY because the default had no POs. */
  is_fallback?: boolean;
}
export interface PoRegisterParams {
  fy?: string;
  status?: "open" | "closed" | "all";
  committee?: string;
  q?: string;
  page?: number;
  sort?: "order_date" | "total";
  dir?: "asc" | "desc";
}

/** Build the /api/finance/po-register query string from filter params. */
export function poRegisterQuery(p: PoRegisterParams): string {
  const sp = new URLSearchParams();
  if (p.fy) sp.set("fy", p.fy);
  if (p.status) sp.set("status", p.status);
  if (p.committee) sp.set("committee", p.committee);
  if (p.q) sp.set("q", p.q);
  if (p.page && p.page > 1) sp.set("page", String(p.page));
  if (p.sort) sp.set("sort", p.sort);
  if (p.dir) sp.set("dir", p.dir);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** GET /api/finance/po-register — server-side filtered + paginated PO register. */
export function usePoRegister(p: PoRegisterParams, intervalMs = 120_000) {
  return usePolling<PoRegisterResponse>(
    `/api/finance/po-register${poRegisterQuery(p)}`,
    intervalMs,
  );
}

/** GET /api/finance/po-committees — distinct committees for the filter dropdown. */
export function usePoCommittees(intervalMs = 300_000) {
  return usePolling<{ committees: string[] }>(
    "/api/finance/po-committees",
    intervalMs,
  );
}

export interface Fy27BudgetLine {
  id: number;
  ol3_code: string;
  ol3_desc: string | null;
  fund_type: string;
  committee: string | null;
  fy26_budget: number;
  fy26_actual: number;
  fy27_requested: number;
  justification: string | null;
  status: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface Fy27BudgetResponse {
  lines: Fy27BudgetLine[];
  summary: {
    line_count: number;
    fy26_budget_total: number;
    fy26_actual_total: number;
    fy27_requested_total: number;
    approved_count: number;
    submitted_count: number;
    by_fund: Record<
      string,
      {
        fy26_budget: number;
        fy26_actual: number;
        fy27_requested: number;
        count: number;
      }
    >;
  };
}

export interface HistoryYearRow {
  fiscal_year: string;
  exp_budget: number;
  exp_actual: number;
  exp_available: number;
  rev_actual: number;
  [key: string]: unknown;
}
export interface HistoryObjectRow {
  fiscal_year: string;
  object_segment: string;
  object_desc: string | null;
  budget_amount: number;
  actual_amount: number;
  [key: string]: unknown;
}
export interface FinanceHistoryResponse {
  by_year: HistoryYearRow[];
  object_rows: HistoryObjectRow[];
}

/** GET /api/finance/history — multi-year (FY20–FY26) object-level history. */
export function useFinanceHistory(intervalMs = 300_000) {
  return usePolling<FinanceHistoryResponse>("/api/finance/history", intervalMs);
}

export interface CommitteeFyRow {
  committee: string;
  fiscal_year: string;
  total: number;
  po_count: number;
  [key: string]: unknown;
}
export interface CommitteeSupplierRow {
  committee: string;
  supplier: string;
  fiscal_year: string;
  total: number;
  [key: string]: unknown;
}
/** Server-computed FY27 'proposed' baseline (F-BASELINE): trailing-3yr recurring
 * PO average (one-time/capital lines stripped) × 1.03, per committee. Single
 * source of truth for FinanceOverview + FinanceBoardPacket. */
export interface ProposedFy27Row {
  committee: string;
  proposed_fy27: number;
  recurring_avg: number;
  stripped_count: number;
  [key: string]: unknown;
}
export interface CommitteesResponse {
  by_committee_fy: CommitteeFyRow[];
  by_supplier: CommitteeSupplierRow[];
  /** per-committee FY27 proposed baseline (includes 'Unassigned'; clients drop it) */
  proposed_fy27: ProposedFy27Row[];
  /** total proposed EXCLUDING 'Unassigned' (the board-quoted figure) */
  total_proposed: number;
}

/** GET /api/finance/committees — committee budget-builder (PO supplier spend by committee × FY). */
export function useCommittees(intervalMs = 300_000) {
  return usePolling<CommitteesResponse>("/api/finance/committees", intervalMs);
}

export interface VendorRow {
  supplier: string;
  committee: string;
  fiscal_year: string;
  total: number;
  po_count: number;
  [key: string]: unknown;
}

/** GET /api/finance/vendors — supplier spend by fiscal year (vendor analysis). */
export function useVendors(intervalMs = 300_000) {
  return usePolling<{ rows: VendorRow[] }>("/api/finance/vendors", intervalMs);
}

// Since the 7/19 baseline fix these lines ARE the board-adopted FY27 v2
// budgets (dash_fy27_v2_line, recurring only): proposed_fy27 == adjusted_fy27
// == adopted v2, projects is null, status 'approved', justification
// 'Board-adopted FY27 v2 budget (06-JUL-2026)'. The frozen pre-v2 table +
// trailing-average baseline are retired server-side.
export interface CommitteeBudgetLine {
  committee: string;
  proposed_fy27: number;
  adjusted_fy27: number;
  projects: number | null;
  justification: string | null;
  status: string;
  updated_at: string;
  [key: string]: unknown;
}
export interface CommitteeBudgetResponse {
  lines: CommitteeBudgetLine[];
  history: { committee: string; fiscal_year: string; total: number }[];
}

/** GET /api/finance/committee-budget — board-adopted FY27 v2 committee budgets + PO history. */
export function useCommitteeBudget(intervalMs = 120_000) {
  return usePolling<CommitteeBudgetResponse>(
    "/api/finance/committee-budget",
    intervalMs,
  );
}

export interface ReconRow {
  account: string;
  period: string;
  status: string;
  reconciled_by: string | null;
  reviewed_by: string | null;
  reviewed_date: string | null;
  notes: string | null;
  [key: string]: unknown;
}
/** GET /api/finance/reconciliation — bank-reconciliation status tracker (§10). */
export function useReconciliation(intervalMs = 300_000) {
  return usePolling<{ rows: ReconRow[] }>(
    "/api/finance/reconciliation",
    intervalMs,
  );
}
/** PATCH one account×period reconciliation. */
export async function saveReconciliation(
  account: string,
  period: string,
  patch: {
    status?: string;
    reconciled_by?: string;
    reviewed_by?: string;
    notes?: string;
  },
): Promise<ReconRow> {
  const res = await apiFetch("/api/finance/reconciliation", {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ account, period, ...patch }),
  });
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
  return res.json() as Promise<ReconRow>;
}

export interface AuthorityRow {
  committee: string;
  primary_authority: string | null;
  secondary_authority: string | null;
  updated_at: string;
  [key: string]: unknown;
}
/** GET /api/finance/authority — approval-authority registry (§5.4.6). */
export function useAuthority(intervalMs = 300_000) {
  return usePolling<{ authorities: AuthorityRow[] }>(
    "/api/finance/authority",
    intervalMs,
  );
}
/** PATCH a committee's approval authorities. */
export async function saveAuthority(
  committee: string,
  patch: { primary_authority?: string; secondary_authority?: string },
): Promise<AuthorityRow> {
  const res = await apiFetch("/api/finance/authority", {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ committee, ...patch }),
  });
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
  return res.json() as Promise<AuthorityRow>;
}

/** PATCH a committee's FY27 budget (adjusted ask / projects / justification / status). */
export async function saveCommitteeBudget(
  committee: string,
  patch: {
    adjusted_fy27?: number;
    projects?: number;
    justification?: string;
    status?: string;
  },
): Promise<CommitteeBudgetLine> {
  const res = await apiFetch("/api/finance/committee-budget", {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ committee, ...patch }),
  });
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
  return res.json() as Promise<CommitteeBudgetLine>;
}

export interface ProjectRow {
  id: number;
  name: string;
  committee: string | null;
  category: string;
  fiscal_year: string;
  cost_estimate: number;
  spent_to_date: number;
  status: string;
  board_approved: number;
  approved_date: string | null;
  approved_by: string | null;
  approved_at: string | null;
  board_motion_ref: string | null;
  priority: string | null;
  requested_by: string | null;
  target_date: string | null;
  description: string | null;
  justification: string | null;
  notes: string | null;
  created_at: string | null;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  [key: string]: unknown;
}
export interface ProjectsResponse {
  projects: ProjectRow[];
  by_committee: Record<
    string,
    { estimate: number; approved: number; spent: number; count: number }
  >;
}
/** GET /api/finance/projects — capital/non-recurring projects + committee rollup. */
export function useProjects(intervalMs = 120_000) {
  return usePolling<ProjectsResponse>("/api/finance/projects", intervalMs);
}

export interface ProjectsManageResponse {
  projects: ProjectRow[];
  total_count: number;
  total_amount: number;
  by_status: Record<string, { count: number; amount: number }>;
  by_committee: Record<string, { count: number; amount: number }>;
  committees: string[];
  priorities: string[];
}
/** GET /api/finance/projects/manage — board review/approve view: every project
 *  plus totals by status + committee + the valid committee/priority option lists. */
export function useProjectsManage(intervalMs = 120_000) {
  return usePolling<ProjectsManageResponse>(
    "/api/finance/projects/manage",
    intervalMs,
  );
}

export async function createProject(p: {
  name: string;
  committee?: string | null;
  category?: string;
  fiscal_year?: string;
  cost_estimate?: number;
  priority?: string | null;
  requested_by?: string;
  description?: string;
  justification?: string;
  target_date?: string;
}): Promise<ProjectRow> {
  const res = await apiFetch("/api/finance/projects", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) detail = j.error;
    } catch {
      /* keep status */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<ProjectRow>;
}
export async function saveProject(
  id: number,
  patch: Partial<Omit<ProjectRow, "id">>,
): Promise<ProjectRow> {
  const res = await apiFetch(`/api/finance/projects/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) detail = j.error;
    } catch {
      /* keep status */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<ProjectRow>;
}
export async function deleteProject(id: number): Promise<void> {
  const res = await apiFetch(`/api/finance/projects/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Delete failed: HTTP ${res.status}`);
}

/** POST approve (lock) a project — records the approver + optional board motion ref. */
export async function approveProject(
  id: number,
  opts?: { approved_by?: string; board_motion_ref?: string },
): Promise<ProjectRow> {
  const body: Record<string, string> = {};
  if (opts?.approved_by) body.approved_by = opts.approved_by;
  if (opts?.board_motion_ref) body.board_motion_ref = opts.board_motion_ref;
  const res = await apiFetch(`/api/finance/projects/${id}/approve`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Approve failed: HTTP ${res.status}`);
  return res.json() as Promise<ProjectRow>;
}
/** POST reject a project — status → rejected. */
export async function rejectProject(id: number): Promise<ProjectRow> {
  const res = await apiFetch(`/api/finance/projects/${id}/reject`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Reject failed: HTTP ${res.status}`);
  return res.json() as Promise<ProjectRow>;
}
/** POST reopen a project — status → proposed, clears the approval snapshot. */
export async function reopenProject(id: number): Promise<ProjectRow> {
  const res = await apiFetch(`/api/finance/projects/${id}/reopen`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Reopen failed: HTTP ${res.status}`);
  return res.json() as Promise<ProjectRow>;
}

// ── Recurring finance analysis (scheduled "what changed" engine) ──────────────

export interface FinanceSignal {
  category: "budget_control" | "vendor_anomaly";
  severity: "critical" | "warning" | "info";
  subject_key: string;
  subject_label: string | null;
  message: string;
  metric_value: number | null;
  metrics: Record<string, number>;
  delta_state:
    | "new"
    | "worsening"
    | "improving"
    | "unchanged"
    | "resolved"
    | null;
  delta_since_last: number | null;
  [key: string]: unknown;
}
export interface FinanceAnalysisRun {
  id: number;
  run_at: string;
  trigger: string;
  signal_count: number;
  summary: {
    total?: number;
    critical?: number;
    warning?: number;
    by_state?: Record<string, number>;
    [k: string]: unknown;
  };
}
export interface FinanceAnalysisResponse {
  run: FinanceAnalysisRun | null;
  signals: FinanceSignal[];
}

/** GET /api/finance/analysis — latest scheduled-analysis run + its signals. */
export function useFinanceAnalysis(intervalMs = 300_000) {
  return usePolling<FinanceAnalysisResponse>(
    "/api/finance/analysis",
    intervalMs,
  );
}

/** POST /api/finance/analysis/run — admin-only manual refresh (returns the new run). */
export async function runFinanceAnalysisNow(): Promise<{
  ran: boolean;
  run_id?: number;
  signal_count?: number;
  reason?: string;
}> {
  const res = await apiFetch("/api/finance/analysis/run", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Run failed: HTTP ${res.status}`);
  return res.json() as Promise<{
    ran: boolean;
    run_id?: number;
    signal_count?: number;
    reason?: string;
  }>;
}

/** GET /api/finance/budget/fy27 — FY27 budget lines + summary. */
export function useFy27Budget(intervalMs = 120_000) {
  return usePolling<Fy27BudgetResponse>("/api/finance/budget/fy27", intervalMs);
}

/** PATCH a single FY27 budget line. Returns the updated row (throws on error). */
export async function saveFy27BudgetLine(
  id: number,
  patch: { fy27_requested?: number; justification?: string; status?: string },
): Promise<Fy27BudgetLine> {
  const res = await apiFetch(`/api/finance/budget/fy27/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
  return res.json() as Promise<Fy27BudgetLine>;
}

// ── FY27 Budget Build & Govern (migration 0068) ───────────────────────────────
// The supplier/OCA-line committee build with a per-committee approval lock + the
// every-BOD unbudgeted governance view. Recurring operating total reconciles to
// $3,435,100 (served as `target`); capital + one-time lines are surfaced
// separately and NEVER summed into the recurring headline.

export interface Fy27BuildLine {
  id: number;
  committee: string;
  committee_order: number;
  sort_order: number;
  label: string;
  oca_code: string | null;
  fy24_actual: number | null;
  fy25_actual: number | null;
  fy26_actual: number | null;
  fy27_proposed: number | null;
  fy27_approved: number | null;
  is_capital: number;
  is_one_time: number;
  is_placeholder: number;
  justification: string | null;
  /** Exact dash_finance_po.supplier name for this line (NULL = aggregate/reserve/
   *  capital/placeholder line with no single supplier; not expandable). */
  po_supplier_key: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface Fy27BuildCommittee {
  committee: string;
  committee_order: number;
  status: "draft" | "approved";
  approved_at: string | null;
  approved_by: string | null;
  lines: Fy27BuildLine[];
  recurring_proposed_total: number;
  recurring_approved_total: number;
  fy26_recurring_total: number;
  placeholder_count: number;
  line_count: number;
}

export interface Fy27BuildResponse {
  committees: Fy27BuildCommittee[];
  capital_lines: Fy27BuildLine[];
  one_time_lines: Fy27BuildLine[];
  target: number;
  grand_total_recurring_proposed: number;
  grand_total_recurring_approved: number;
  fy26_recurring_total: number;
  capital_one_time_total: number;
  one_time_total: number;
  approved_committees: number;
  total_committees: number;
}

/** GET /api/finance/fy27/budget — the FY27 committee budget build. */
export function useFy27Build(intervalMs = 120_000) {
  return usePolling<Fy27BuildResponse>("/api/finance/fy27/budget", intervalMs);
}

export interface Fy27V2Spend {
  channel: string;
  label: string;
  fy25: number;
  fy26: number;
}
export interface Fy27V2Line {
  id: number;
  committee: string;
  category: string;
  expense_type: string;
  fy24_actual: number;
  fy25_actual: number;
  fy26_actual: number;
  fy27_v2: number;
  fy27_v1: number;
  is_capital: number;
  oca_codes: string | null;
  backing: string | null;
  supplier_detail: string | null;
  reasoning: string | null;
  needs_director_confirm: number;
  spend: Fy27V2Spend[];
}
export interface Fy27V2Category {
  category: string;
  sub: {
    fy24: number;
    fy25: number;
    fy26: number;
    fy27_v2: number;
    fy27_v1: number;
  };
  lines: Fy27V2Line[];
}
/** FY26 arrears exposure (un-entered invoices, no PO) — informational only,
 *  NEVER additive. Sourced from dash_fy27_v2_carryover (misnamed table: these
 *  are NOT carryover — the open-PO encumbrance join is the carryover layer). */
export interface Fy27V2Arrears {
  label: string;
  amount: number;
  basis: string | null;
}
/** One FY26 open PO in a committee's carryover (encumbrance) layer. */
export interface Fy27V2CarryoverPo {
  po_number: string;
  supplier: string | null;
  remaining: number;
}
export interface Fy27V2Committee {
  committee: string;
  committee_order: number;
  v1_fy27: number;
  fy26_actual: number;
  status: string;
  /** Synthetic card for POs/projects whose committee is null or unmapped. */
  is_unassigned: boolean;
  recurring: number;
  delta_v1: number;
  needs_confirm: number;
  /** Layer 2 — funded one-time projects (ADDITIVE to recurring). */
  projects_funded: Fy27V2Project[];
  projects_funded_total: number;
  /** Operating-levy projects already inside recurring lines — tagged, not re-added. */
  projects_in_recurring: Fy27V2Project[];
  /** Rejected/Deferred — collapsed "not funded" note. */
  projects_not_funded: Fy27V2Project[];
  /** Layer 3 — FY26→FY27 open-PO carryover (confirmed at FY close). */
  po_carryover: { total: number; count: number; pos: Fy27V2CarryoverPo[] };
  arrears: Fy27V2Arrears[];
  arrears_total: number;
  arrears_note: string | null;
  /** G-3: recurring + projects_funded_total + po_carryover.total (arrears excluded). */
  all_in: number;
  categories: Fy27V2Category[];
}
export interface Fy27V2ChartRow {
  committee: string;
  fy26_actual: number;
  fy27_v2: number;
}
export interface Fy27V2Project {
  ref: string;
  name: string;
  amount: number;
  funding_source: string | null;
  committee: string | null;
  status: string | null;
  affects_operating: number;
  source_note: string | null;
}
export interface Fy27V2Response {
  meta: Record<string, string>;
  grand: {
    recurring: number;
    v1_recurring: number;
    delta: number;
    capital: number;
    projects_funded: number;
    po_carryover: number;
    po_carryover_count: number;
    all_in: number;
  };
  /** Shared layer disclosures — render these exact labels on every surface. */
  labels: {
    po_carryover: string;
    po_carryover_footnote: string;
    arrears: string;
    all_in: string;
  };
  /** G-5: source committee names that failed to normalize (shown as Unassigned). */
  unmapped_committees: { name: string; count: number }[];
  /** Open POs with an unparseable FY suffix, included in carryover conservatively. */
  po_carryover_unparsed: number;
  chart: Fy27V2ChartRow[];
  committees: Fy27V2Committee[];
  capital: Fy27V2Line[];
  projects: Fy27V2Project[];
  oca_rollup: { oca: string; amount: number }[];
}
export function useFy27V2(intervalMs = 120_000) {
  return usePolling<Fy27V2Response>("/api/finance/fy27/v2", intervalMs);
}

/** PATCH a build line's FY27 ask ("move the bar"). Pass null to clear a placeholder.
 *  Rejected (409) if the line's committee is approved/locked. */
export async function saveFy27Line(
  id: number,
  fy27_proposed: number | null,
): Promise<Fy27BuildLine> {
  const res = await apiFetch(`/api/finance/fy27/line/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fy27_proposed }),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) detail = j.error;
    } catch {
      /* keep status */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<Fy27BuildLine>;
}

/** POST approve (lock) a committee — snapshots the proposed asks into approved.
 *  `approvedBy` is the chosen approver (a director/officer email/identifier from the
 *  user directory); when omitted the server records the acting session. */
export async function approveFy27Committee(
  committee: string,
  approvedBy?: string,
): Promise<Fy27BuildCommittee> {
  const res = await apiFetch(
    `/api/finance/fy27/committee/${encodeURIComponent(committee)}/approve`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(approvedBy ? { approved_by: approvedBy } : {}),
    },
  );
  if (!res.ok) throw new Error(`Approve failed: HTTP ${res.status}`);
  return res.json() as Promise<Fy27BuildCommittee>;
}

// ── FY27 supplier-PO drill-down + approver directory ──────────────────────────

export interface Fy27LinePo {
  po_number: string;
  supplier: string | null;
  total: number;
  description: string | null;
  status: string | null;
  order_date: string | null;
}
export interface Fy27LinePosResponse {
  supplier: string | null;
  pos: Fy27LinePo[];
  count: number;
  total: number;
  note?: string;
}

/** GET /api/finance/fy27/line/:id/pos — the FY26 POs behind a line's supplier.
 *  Lazy-loaded on expand (NOT polled). Returns an empty list + note for aggregate/
 *  reserve lines that have no single supplier. */
export async function fetchFy27LinePos(
  id: number,
): Promise<Fy27LinePosResponse> {
  const res = await apiFetch(`/api/finance/fy27/line/${id}/pos`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<Fy27LinePosResponse>;
}

export interface UserDirectoryEntry {
  id: number;
  email: string;
  name: string;
}
/** GET /api/users/directory — active-user roster for the approver picker. */
export function useUserDirectory(intervalMs = 300_000) {
  return usePolling<{ users: UserDirectoryEntry[]; count: number }>(
    "/api/users/directory",
    intervalMs,
  );
}

/** POST reopen a committee for editing — clears the approved snapshot. */
export async function reopenFy27Committee(
  committee: string,
): Promise<Fy27BuildCommittee> {
  const res = await apiFetch(
    `/api/finance/fy27/committee/${encodeURIComponent(committee)}/reopen`,
    { method: "POST", credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`Reopen failed: HTTP ${res.status}`);
  return res.json() as Promise<Fy27BuildCommittee>;
}

export interface Fy27TrackingRow {
  committee: string;
  committee_order: number;
  status: "draft" | "approved";
  budget: number;
  budget_basis: "approved" | "proposed";
  spend_to_date: number | null;
  variance: number | null;
}
export interface Fy27UnbudgetedItem {
  id: number;
  committee: string | null;
  description: string;
  amount: number;
  board_approved_date: string | null;
  motion_ref: string | null;
  created_at: string | null;
  created_by: string | null;
}
export interface Fy27TrackingResponse {
  fy27_started: boolean;
  fy27_start_date: string;
  tracking: Fy27TrackingRow[];
  unbudgeted: Fy27UnbudgetedItem[];
  unbudgeted_total: number;
}

/** GET /api/finance/fy27/tracking — performance-vs-budget + unbudgeted items. */
export function useFy27Tracking(intervalMs = 120_000) {
  return usePolling<Fy27TrackingResponse>(
    "/api/finance/fy27/tracking",
    intervalMs,
  );
}

/** POST a mid-year board-approved unbudgeted item. */
export async function createFy27Unbudgeted(item: {
  committee?: string | null;
  description: string;
  amount: number;
  board_approved_date?: string;
  motion_ref?: string;
}): Promise<Fy27UnbudgetedItem> {
  const res = await apiFetch("/api/finance/fy27/unbudgeted", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(item),
  });
  if (!res.ok) throw new Error(`Add failed: HTTP ${res.status}`);
  return res.json() as Promise<Fy27UnbudgetedItem>;
}

/** DELETE an unbudgeted item. */
export async function deleteFy27Unbudgeted(id: number): Promise<void> {
  const res = await apiFetch(`/api/finance/fy27/unbudgeted/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Delete failed: HTTP ${res.status}`);
}

// ── FY27 Operating Bridge (migrations 0072 + 0073) ────────────────────────────
// The build-up from the recurring base ($3,435,100) to the FY27 operating need.
// All totals are computed server-side; the page reads them, never hardcodes them.

/** A project-derived bridge line (operating add / restricted / insurance / rejected). */
export interface Fy27BridgeProjectLine {
  id: number;
  label: string;
  amount: number;
  status: string | null;
}

/** An editable, non-initiative bridge adjustment (amount NULL = pending RJ input). */
export interface Fy27BridgeAdjustment {
  id: number;
  sort_order: number;
  category: string;
  label: string;
  amount: number | null;
  source_note: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface Fy27BridgeResponse {
  base: number;
  approved_operating_adds: Fy27BridgeProjectLine[];
  approved_operating_adds_total: number;
  rr_shift: Fy27BridgeProjectLine | null;
  adjustments: Fy27BridgeAdjustment[];
  adjustments_total: number;
  /** Count of operating adds whose amount isn't entered yet — the operating need
   *  is a confirmed FLOOR and rises as these are filled. */
  pending_count: number;
  restricted: Fy27BridgeProjectLine[];
  insurance: Fy27BridgeProjectLine[];
  rejected: Fy27BridgeProjectLine[];
  operating_total: number;
}

/** GET /api/finance/fy27/bridge — the FY27 operating bridge build-up. */
export function useFy27Bridge(intervalMs = 120_000) {
  return usePolling<Fy27BridgeResponse>("/api/finance/fy27/bridge", intervalMs);
}

/** PATCH a bridge adjustment's amount (pass null to clear it back to pending). */
export async function saveFy27BridgeAdjustment(
  id: number,
  amount: number | null,
): Promise<Fy27BridgeAdjustment> {
  const res = await apiFetch(`/api/finance/fy27/bridge/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount }),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) detail = j.error;
    } catch {
      /* keep status */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<Fy27BridgeAdjustment>;
}

// ── Deficit / Carryover tracker (migration 0074) ──────────────────────────────
// Unpaid FY26 vendor invoices that bleed into FY27 and hit individual committee
// budgets. All totals are computed server-side (GET /api/finance/deficit); the
// page reads them, never hardcodes them. The raw ledger drives the per-vendor
// monthly tracker, with edit/add via PATCH/POST.

export type InvoiceStatus = "paid" | "verify" | "pending";

export interface InvoiceTrackingRow {
  id: number;
  vendor: string;
  committee: string | null;
  fiscal_year: string;
  period_month: string | null;
  amount: number | null; // NULL = TBD (billed amount unknown)
  status: InvoiceStatus;
  po_number: string | null;
  action: string | null; // 'Pay' | 'No Money' | null
  bleeds_to_next_fy: number; // 1 = unpaid → carries to FY27
  note: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface DeficitVendor {
  vendor: string;
  committee: string | null;
  billed_total: number;
  paid_total: number;
  unpaid_total: number;
  tbd_count: number;
  po_number: string | null;
  po_coverage: number;
  uncovered: number;
}

export interface DeficitCommittee {
  committee: string;
  unpaid_carryover: number;
  fy27_budget: number;
  projected_need: number;
}

export interface DeficitResponse {
  by_vendor: DeficitVendor[];
  by_committee: DeficitCommittee[];
  total_unpaid_carryover: number;
  total_billed: number;
  total_paid: number;
  total_tbd_count: number;
  vendor_count: number;
}

/** GET /api/finance/deficit — the by-vendor + by-committee carryover rollup. */
export function useDeficit(intervalMs = 120_000) {
  return usePolling<DeficitResponse>("/api/finance/deficit", intervalMs);
}

/** GET /api/finance/invoices — the raw invoice ledger (optionally for one vendor). */
export function useInvoices(vendor?: string, intervalMs = 120_000) {
  const url = vendor
    ? `/api/finance/invoices?vendor=${encodeURIComponent(vendor)}`
    : "/api/finance/invoices";
  return usePolling<{ rows: InvoiceTrackingRow[]; count: number }>(
    url,
    intervalMs,
  );
}

export interface InvoiceInput {
  vendor: string;
  committee?: string | null;
  fiscal_year: string;
  period_month?: string | null;
  amount?: number | null;
  status?: InvoiceStatus;
  po_number?: string | null;
  action?: string | null;
  bleeds_to_next_fy?: number;
  note?: string | null;
}

async function readInvoiceError(res: Response): Promise<string> {
  let detail = `HTTP ${res.status}`;
  try {
    const j = (await res.json()) as { error?: string };
    if (j.error) detail = j.error;
  } catch {
    /* keep status */
  }
  return detail;
}

/** POST a new invoice row (new vendor or new month). */
export async function createInvoice(
  item: InvoiceInput,
): Promise<InvoiceTrackingRow> {
  const res = await apiFetch("/api/finance/invoices", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(item),
  });
  if (!res.ok) throw new Error(await readInvoiceError(res));
  return res.json() as Promise<InvoiceTrackingRow>;
}

/** PATCH an invoice row — only the supplied fields are changed. Common case:
 *  mark paid → { status: 'paid', bleeds_to_next_fy: 0 }. */
export async function updateInvoice(
  id: number,
  patch: Partial<
    Pick<
      InvoiceTrackingRow,
      "amount" | "status" | "action" | "bleeds_to_next_fy" | "note"
    >
  >,
): Promise<InvoiceTrackingRow> {
  const res = await apiFetch(`/api/finance/invoices/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await readInvoiceError(res));
  return res.json() as Promise<InvoiceTrackingRow>;
}

/** DELETE an invoice row. */
export async function deleteInvoice(id: number): Promise<void> {
  const res = await apiFetch(`/api/finance/invoices/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(await readInvoiceError(res));
}
